import { useState } from "react";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const NumField = ({ label, value, onChange, testid }) => (
  <label className="block space-y-1"><span className="text-xs c-text-2">{label}</span>
    <input data-testid={testid} type="number" min="1" inputMode="numeric" className="c-input text-sm" value={value} onChange={(e) => onChange(e.target.value)} />
  </label>
);

export const ConfigControls = ({ m, onSaved }) => {
  const [f, setF] = useState({ cupo_fundadores: m.fundadores.cupo, limite_busquedas_dia: m.limites.limite_busquedas_dia, limite_solicitudes_dia: m.limites.limite_solicitudes_dia });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { await api.patch("/admin/config", Object.fromEntries(Object.entries(f).map(([k, v]) => [k, Number(v)]))); toast.success("Configuración guardada"); onSaved(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="admin-config-controls" className="c-card p-3.5 space-y-2.5">
      <p className="font-mono text-[10px] tracking-[0.2em] uppercase c-muted">Cupo y límites diarios</p>
      <div className="grid grid-cols-3 gap-2">
        <NumField label="Cupo fundadores" value={f.cupo_fundadores} onChange={set("cupo_fundadores")} testid="admin-cupo-input" />
        <NumField label="Búsquedas/día" value={f.limite_busquedas_dia} onChange={set("limite_busquedas_dia")} testid="admin-limit-search-input" />
        <NumField label="Solicitudes/día" value={f.limite_solicitudes_dia} onChange={set("limite_solicitudes_dia")} testid="admin-limit-requests-input" />
      </div>
      <button data-testid="admin-config-save-btn" disabled={busy} onClick={save} className="c-btn-primary w-full py-2.5 flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar</button>
    </div>
  );
};

export const ExtraInvites = ({ onSaved }) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [n, setN] = useState(5);
  const search = async () => { try { setItems((await api.get("/admin/usuarios", { params: { q } })).data.items); } catch (e) { toast.error(errMsg(e)); } };
  const give = async (u) => {
    try { const { data } = await api.post("/admin/invitaciones", { user_id: u.id, cantidad: Number(n) }); toast.success(`${u.nombre_publico} ahora tiene ${data.invitaciones_disponibles} invitaciones`); search(); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-extra-invites" className="c-card p-3.5 space-y-2.5">
      <p className="font-mono text-[10px] tracking-[0.2em] uppercase c-muted">Invitaciones extra</p>
      <div className="flex gap-2">
        <input data-testid="admin-user-search-input" className="c-input text-sm flex-1" placeholder="Usuario o nombre público" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} />
        <input data-testid="admin-extra-qty-input" type="number" min="1" className="c-input text-sm !w-16" value={n} onChange={(e) => setN(e.target.value)} />
        <button data-testid="admin-user-search-btn" onClick={search} className="px-3 rounded-xl border c-line"><Search className="w-4 h-4" /></button>
      </div>
      {items?.map((u) => (
        <div key={u.id} data-testid="admin-user-result" className="flex items-center gap-2 text-sm">
          <span className="flex-1 min-w-0 truncate"><span className="font-semibold">{u.nombre_publico}</span> <span className="text-xs c-muted">@{u.usuario} · {u.invitaciones_disponibles ?? "∞"}</span></span>
          {u.invitaciones_disponibles != null && <button data-testid="admin-give-invites-btn" onClick={() => give(u)} className="text-xs font-semibold c-accent px-2 py-1 rounded-lg border c-line">+{n}</button>}
        </div>
      ))}
      {items?.length === 0 && <p className="text-xs c-muted">Sin resultados.</p>}
    </div>
  );
};

export const AuditList = ({ items }) => (
  <div data-testid="admin-audit-list" className="c-card p-3.5 space-y-1.5">
    <p className="font-mono text-[10px] tracking-[0.2em] uppercase c-muted">Registro de cambios</p>
    {items.length === 0 ? <p className="text-sm c-text-2">Sin cambios todavía.</p> : items.map((a) => (
      <p key={a.id} data-testid="admin-audit-item" className="text-xs"><span className="font-semibold">{a.actor}</span> · {a.accion === "config" ? `config: ${Object.entries(a.detalle).map(([k, v]) => `${k} ${v.antes ?? "—"}→${v.despues}`).join(", ")}` : `+${a.detalle.cantidad} invitaciones a @${a.detalle.usuario}`}
        <span className="c-muted"> · {new Date(a.fecha).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}</span></p>
    ))}
  </div>
);
